// src/features/customers/customerSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Customer, fetchCustomers, CustomerFilters } from "../../services/customer";

/* =======================
   STATE
======================= */

interface CustomersState {
  customers: Customer[];
  loading: boolean;
  hasFetched: boolean;
  error?: string;
  total: number;

  page: number;
  pageSize: number;
  searchQuery: string;
  sortField: string;
  sortOrder: "asc" | "desc";
  filters: CustomerFilters;
}

const initialState: CustomersState = {
  customers: [],
  loading: false,
  hasFetched: false,
  total: 0,

  page: 1,
  pageSize: 10,
  searchQuery: "",
  sortField: "createdAt",
  sortOrder: "desc",
  filters: {
    platform: "",
    dateRange: { from: undefined, to: undefined },
    partner : ""
  },
};

/* =======================
   THUNK (WITHOUT GENERIC)
======================= */

export const fetchCustomersThunk = createAsyncThunk(
  "customers/fetchCustomers",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { customers: CustomersState; auth: { token: string } };
      const { page, pageSize, searchQuery, sortField, sortOrder, filters } = state.customers;
      const { token } = state.auth;

      const response = await fetchCustomers({
        page,
        pageSize,
        sortField,
        sortOrder,
        search: searchQuery,
        filters,
        token,
      });

      return {
        customers: response.customers,
        total: response.totalMatch,
        page: response.pageNo ?? page,
        pageSize: response.pageSize ?? pageSize,
      };
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch customers");
    }
  }
);

/* =======================
   SLICE
======================= */

const customerSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortField = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<"asc" | "desc">) => {
      state.sortOrder = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setFilters: (state, action: PayloadAction<CustomerFilters>) => {
      state.filters = action.payload;
      state.page = 1;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.page = 1;
    },
    setHasFetched: (state, action: PayloadAction<boolean>) => {
      state.hasFetched = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomersThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchCustomersThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload.customers;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.hasFetched = true;
      })
      .addCase(fetchCustomersThunk.rejected, (state, action) => {
        state.loading = false;
        state.customers = [];
        state.total = 0;
        state.error = action.payload as string;
      });
  },
});

/* =======================
   EXPORTS
======================= */

export const {
  setPage,
  setPageSize,
  setSortBy,
  setSortOrder,
  setSearchQuery,
  setFilters,
  resetFilters,
  setHasFetched,
} = customerSlice.actions;

export default customerSlice.reducer;
